import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Zap,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Mail,
  UserCheck,
  FileText,
  Send,
  User,
  ClipboardCheck,
  Calendar,
  ArrowRight,
  Clock,
  Activity,
  AlertCircle,
  Sparkles,
  Shield,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  Info,
  Bell,
  TrendingUp,
  PlayCircle,
  Workflow,
  Link2
} from "lucide-react";
import AppSidebar from "@/components/AppSidebar";

// ===================== CONFIG =====================

const TRIGGER_TYPES = {
  form_submitted: { label: "Form Submitted", icon: FileText, color: "bg-blue-100 text-blue-700", requiresFormSelection: true },
  lead_form_submitted: { label: "Lead Form Submitted", icon: FileText, color: "bg-amber-100 text-amber-700", requiresFormSelection: false, description: "When a pre-booking lead form is submitted (includes AI score)" },
  status_changed: { label: "Status Changed", icon: PlayCircle, color: "bg-purple-100 text-purple-700" }
};

const ACTION_TYPES = {
  send_email: { label: "Send Email to Contact", icon: Mail, color: "bg-green-100 text-green-700" },
  send_business_notification: { label: "Notify Business Owner", icon: Bell, color: "bg-amber-100 text-amber-700", description: "Send notification email to you" },
  send_form: { label: "Send Form", icon: Send, color: "bg-cyan-100 text-cyan-700" },
  update_status: { label: "Update Status", icon: UserCheck, color: "bg-purple-100 text-purple-700" },
  update_contact_info: { label: "Update Contact Info", icon: User, color: "bg-orange-100 text-orange-700" }
};

const LEAD_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" }
];

const INTAKE_STATUS_OPTIONS = [
  { value: "awaiting_intake", label: "Awaiting Intake" },
  { value: "intake_requested", label: "Intake Requested" },
  { value: "intake_received", label: "Intake Received" },
  { value: "action_required", label: "Action Required" },
  { value: "needs_more_info", label: "Needs More Info" },
  { value: "awaiting_documents", label: "Awaiting Documents" },
  { value: "intake_completed", label: "Intake Completed" },
  { value: "contract_signed", label: "Contract Signed" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_service", label: "In Service" },
  { value: "service_completed", label: "Service Completed" }
];

const STATUS_OPTIONS = [...LEAD_STATUS_OPTIONS.map(s => ({...s, group: "Lead Phase"})), ...INTAKE_STATUS_OPTIONS.map(s => ({...s, group: "Intake Phase"}))];
const CONTACT_INFO_FIELDS = [
  { value: "source", label: "Source" },
  { value: "service_type", label: "Service Type" },
  { value: "priority", label: "Priority" }
];

// Auto Flows preset groupings
const AUTO_FLOW_GROUPS = [
  {
    id: "lead_handling",
    title: "Lead Handling",
    description: "When new leads come in",
    presets: ["auto_reply", "notify_lead_form_submitted"]
  },
  {
    id: "intake",
    title: "Intake",
    description: "During the intake process",
    presets: ["send_intake", "request_documents", "action_required"]
  },
  {
    id: "conversion",
    title: "Conversion",
    description: "Moving clients forward",
    presets: ["intake_completed", "contract_signed"]
  },
  {
    id: "delivery",
    title: "Delivery",
    description: "When service is complete",
    presets: ["service_completed"]
  }
];

const PRESET_ICONS = {
  auto_reply: { icon: Mail, color: "bg-blue-100 text-blue-700" },
  notify_lead_form_submitted: { icon: Bell, color: "bg-amber-100 text-amber-700" },
  send_intake: { icon: FileText, color: "bg-purple-100 text-purple-700" },
  request_documents: { icon: FileText, color: "bg-orange-100 text-orange-700" },
  action_required: { icon: AlertCircle, color: "bg-yellow-100 text-yellow-700" },
  intake_completed: { icon: ClipboardCheck, color: "bg-green-100 text-green-700" },
  contract_signed: { icon: Calendar, color: "bg-cyan-100 text-cyan-700" },
  service_completed: { icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" }
};

// ===================== HELPERS =====================

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const getAutomationTypeLabel = (type) => {
  const labels = {
    "status_change": "Email Sent",
    "reminder": "Reminder",
    "escalation": "Escalation",
    "ai_followup": "AI Follow-up"
  };
  return labels[type] || type;
};

const getAutomationTypeColor = (type) => {
  if (type === "escalation") return "bg-red-100 text-red-700";
  if (type === "ai_followup") return "bg-purple-100 text-purple-700";
  if (type?.includes("reminder")) return "bg-yellow-100 text-yellow-700";
  return "bg-green-100 text-green-700";
};

// ===================== COMPONENT =====================

export default function AutomationsPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [automations, setAutomations] = useState([]);
  const [customAutomations, setCustomAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [forms, setForms] = useState([]);
  
  // Presets state
  const [presets, setPresets] = useState({});
  const [presetStats, setPresetStats] = useState({});
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [togglingPreset, setTogglingPreset] = useState(null);
  
  // Activity log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Status mapping state
  const [statusMapping, setStatusMapping] = useState([]);
  const [systemAutomations, setSystemAutomations] = useState([]);
  
  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preset detail modal
  const [presetDetailOpen, setPresetDetailOpen] = useState(false);
  const [viewingPreset, setViewingPreset] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    trigger_type: "status_changed",
    trigger_config: {},
    actions: [],
    is_active: true
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [automationToDelete, setAutomationToDelete] = useState(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState("autoflows");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([
      fetchAutomations(),
      fetchCustomAutomations(),
      fetchPresets(),
      fetchPresetStats(),
      fetchTemplates(),
      fetchForms(),
      fetchActivityLogs(),
      fetchStatusMapping()
    ]);
  };

  const fetchPresets = async () => {
    try {
      setPresetsLoading(true);
      const response = await axios.get(`${API}/automations/presets`, { headers: { Authorization: `Bearer ${token}` } });
      setPresets(response.data.presets || {});
    } catch (error) {
      console.error("Failed to fetch presets:", error);
    } finally {
      setPresetsLoading(false);
    }
  };

  const fetchPresetStats = async () => {
    try {
      const response = await axios.get(`${API}/automations/presets/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setPresetStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to fetch preset stats:", error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setActivityLoading(true);
      const response = await axios.get(`${API}/automations/activity?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setActivityLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchStatusMapping = async () => {
    try {
      const response = await axios.get(`${API}/automations/presets/mapping`, { headers: { Authorization: `Bearer ${token}` } });
      setStatusMapping(response.data.status_mapping || []);
      setSystemAutomations(response.data.system_automations || []);
    } catch (error) {
      console.error("Failed to fetch status mapping:", error);
    }
  };

  const handleTogglePreset = async (presetId, currentEnabled) => {
    const newEnabled = !currentEnabled;
    // Optimistic update
    setPresets(prev => ({
      ...prev,
      [presetId]: { ...prev[presetId], enabled: newEnabled }
    }));
    try {
      setTogglingPreset(presetId);
      const formId = forms.length > 0 ? forms[0].id : null;
      await axios.post(`${API}/automations/presets/${presetId}/toggle`, { enabled: newEnabled, form_id: formId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newEnabled ? "Automation enabled" : "Automation disabled");
      await Promise.all([fetchPresets(), fetchAutomations(), fetchCustomAutomations()]);
    } catch (error) {
      // Revert on failure
      setPresets(prev => ({
        ...prev,
        [presetId]: { ...prev[presetId], enabled: currentEnabled }
      }));
      toast.error("Couldn't save change");
    } finally {
      setTogglingPreset(null);
    }
  };

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/automations`, { headers: { Authorization: `Bearer ${token}` } });
      setAutomations(response.data.automations || []);
    } catch (error) {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomAutomations = async () => {
    try {
      const response = await axios.get(`${API}/automations/custom`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomAutomations(response.data.automations || []);
    } catch (error) {
      console.error("Failed to load custom automations", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/workflow/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to load templates", error);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/business/forms`, { headers: { Authorization: `Bearer ${token}` } });
      setForms(response.data.forms || []);
    } catch (error) {
      console.error("Failed to load forms", error);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setEditingAutomation(null);
    setFormData({ name: "", trigger_type: "status_changed", trigger_config: {}, actions: [], is_active: true });
    setModalOpen(true);
  };

  const handleEdit = (automation) => {
    setEditMode(true);
    setEditingAutomation(automation);
    let triggerType = automation.trigger_type === "submission_received" ? "form_submitted" : automation.trigger_type;
    const mappedActions = (automation.actions || []).map(action => {
      if (action.type === "update_client_data") {
        if (action.config?.key === "status" || [...LEAD_STATUS_OPTIONS, ...INTAKE_STATUS_OPTIONS].some(s => s.value === action.config?.value)) {
          return { type: "update_status", config: { status: action.config?.value || "new" } };
        }
        return { type: "update_contact_info", config: { field: action.config?.key || "source", value: action.config?.value || "" } };
      }
      return action;
    });
    setFormData({ name: automation.name, trigger_type: triggerType, trigger_config: automation.trigger_config || {}, actions: mappedActions, is_active: automation.is_active });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Please enter an automation name"); return; }
    if (formData.actions.length === 0) { toast.error("Please add at least one action"); return; }
    if (formData.trigger_type === "form_submitted" && !formData.trigger_config.form_id) { toast.error("Please select a form"); return; }
    const statusValue = formData.trigger_config.to_status || formData.trigger_config.target_status;
    if (formData.trigger_type === "status_changed" && !statusValue) { toast.error("Please select a target status"); return; }

    for (const action of formData.actions) {
      if (action.type === "send_email" && !action.config?.template_id) { toast.error("Please select an email template"); return; }
      if (action.type === "send_business_notification" && !action.config?.template_id) { toast.error("Please select a notification template"); return; }
      if (action.type === "send_form" && !action.config?.form_id) { toast.error("Please select a form to send"); return; }
      if (action.type === "update_status" && !action.config?.status) { toast.error("Please select a status"); return; }
      if (action.type === "update_contact_info" && (!action.config?.field || !action.config?.value?.trim())) { toast.error("Please complete the contact info update"); return; }
    }

    try {
      setSaving(true);
      const backendActions = formData.actions.map(action => {
        if (action.type === "update_status") return { type: "update_status", status: action.config?.status };
        if (action.type === "update_contact_info") return { type: "update_contact_info", field: action.config?.field, value: action.config?.value || "" };
        if (action.type === "send_form") return { type: "send_form", form_id: action.config?.form_id };
        if (action.type === "send_email") return { type: "send_email", template_id: action.config?.template_id };
        if (action.type === "send_business_notification") return { type: "send_business_notification", template_id: action.config?.template_id };
        return action;
      });
      const payload = { name: formData.name, trigger_type: formData.trigger_type, actions: backendActions, is_active: formData.is_active };
      if (formData.trigger_type === "form_submitted") payload.form_id = formData.trigger_config?.form_id;
      else if (formData.trigger_type === "status_changed") payload.to_status = formData.trigger_config?.to_status || formData.trigger_config?.target_status;
      // lead_form_submitted doesn't require additional config

      if (editMode && editingAutomation) {
        await axios.put(`${API}/automations/${editingAutomation.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Automation updated");
      } else {
        await axios.post(`${API}/automations`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Automation created");
      }
      setModalOpen(false);
      fetchAutomations();
      fetchCustomAutomations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save automation");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (automation) => {
    const prevActive = automation.is_active;
    const newActive = !prevActive;
    // Optimistic update
    setAutomations(prev => prev.map(a => a.id === automation.id ? { ...a, is_active: newActive } : a));
    setCustomAutomations(prev => prev.map(a => a.id === automation.id ? { ...a, is_active: newActive } : a));
    try {
      await axios.put(`${API}/automations/${automation.id}`, { is_active: newActive }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newActive ? "Automation activated" : "Automation paused");
      fetchAutomations();
      fetchCustomAutomations();
    } catch (error) {
      // Revert on failure
      setAutomations(prev => prev.map(a => a.id === automation.id ? { ...a, is_active: prevActive } : a));
      setCustomAutomations(prev => prev.map(a => a.id === automation.id ? { ...a, is_active: prevActive } : a));
      toast.error("Couldn't save change");
    }
  };

  const handleDelete = async () => {
    if (!automationToDelete) return;
    try {
      await axios.delete(`${API}/automations/${automationToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Automation deleted");
      setDeleteDialogOpen(false);
      setAutomationToDelete(null);
      fetchAutomations();
      fetchCustomAutomations();
    } catch (error) {
      toast.error("Failed to delete automation");
    }
  };

  const addAction = (type) => {
    const newAction = { type, config: {} };
    if (type === "update_status") newAction.config = { status: "new" };
    else if (type === "update_contact_info") newAction.config = { field: "source", value: "" };
    setFormData(prev => ({ ...prev, actions: [...prev.actions, newAction] }));
  };

  const updateAction = (index, config) => {
    setFormData(prev => ({ ...prev, actions: prev.actions.map((a, i) => i === index ? { ...a, config: { ...a.config, ...config } } : a) }));
  };

  const removeAction = (index) => {
    setFormData(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== index) }));
  };

  const getActionSummary = (action) => {
    switch (action.type) {
      case "send_email": return templates.find(t => t.id === (action.config?.template_id || action.template_id))?.name || "Send email";
      case "send_form": return forms.find(f => f.id === (action.config?.form_id || action.form_id))?.name || "Send form";
      case "update_status": return STATUS_OPTIONS.find(s => s.value === (action.config?.status || action.status))?.label || "Update status";
      case "update_contact_info": return CONTACT_INFO_FIELDS.find(f => f.value === action.config?.field)?.label || "Update info";
      default: return action.type;
    }
  };

  const getTriggerSummary = (automation) => {
    const config = automation.trigger_config || {};
    const triggerType = automation.trigger_type;
    if (triggerType === "submission_received" || triggerType === "form_submitted") {
      if (config.form_id) return forms.find(f => f.id === config.form_id)?.name || "Specific form";
      return "All forms";
    }
    if (triggerType === "lead_form_submitted") {
      if (config.form_id) return forms.find(f => f.id === config.form_id)?.name || "Specific lead form";
      return "All lead forms";
    }
    if (triggerType === "status_changed") {
      const statusValue = config.to_status || config.target_status;
      return STATUS_OPTIONS.find(s => s.value === statusValue)?.label || statusValue || "(not set)";
    }
    return triggerType;
  };

  const RECOMMENDED_PRESETS = ['auto_reply', 'notify_lead_form_submitted', 'send_intake'];

  // Render single preset row
  const renderPresetRow = (presetId) => {
    const preset = presets[presetId];
    if (!preset) return null;
    
    const iconConfig = PRESET_ICONS[presetId] || { icon: Mail, color: "bg-gray-100 text-gray-700" };
    const Icon = iconConfig.icon;
    const stats = presetStats[presetId] || {};
    const isRecommended = RECOMMENDED_PRESETS.includes(presetId);
    
    return (
      <div key={presetId} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100">
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg ${iconConfig.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{preset.name}</h4>
              {preset.template_label && <Badge variant="outline" className="text-xs">{preset.template_label}</Badge>}
              {isRecommended && <Badge className="bg-primary/10 text-primary text-xs font-normal">Recommended</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">{preset.trigger_display}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              {stats.last_triggered ? (
                <>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Last: {formatTimeAgo(stats.last_triggered)}</span>
                  <span>•</span>
                  <span>{stats.times_triggered} times</span>
                </>
              ) : (
                <span className="italic">Ready — awaiting first event</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" onClick={() => { setViewingPreset({ ...preset, presetId }); setPresetDetailOpen(true); }} data-testid={`preset-edit-${presetId}`}>
            <Edit className="w-4 h-4" />
          </Button>
          {togglingPreset === presetId && <Loader2 className="w-4 h-4 animate-spin" />}
          <Switch checked={preset.enabled || false} onCheckedChange={() => handleTogglePreset(presetId, preset.enabled)} disabled={togglingPreset === presetId} data-testid={`preset-${presetId}`} />
        </div>
      </div>
    );
  };

  // ===================== RENDER =====================

  return (
    <div className="min-h-screen bg-background" data-testid="automations-page">
      <AppSidebar user={user} onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold">Automations</h1>
              <p className="text-muted-foreground mt-1">Configure your automated workflows</p>
            </div>
            <Button onClick={fetchAll} variant="ghost" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
          
          {/* Analytics Shortcut */}
          <Link to="/analytics" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-6">
            <TrendingUp className="w-4 h-4" />
            Track performance in Analytics
            <ArrowRight className="w-3 h-3" />
          </Link>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="autoflows" className="flex items-center gap-2">
                <Workflow className="w-4 h-4" />
                Auto Flows
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                History
              </TabsTrigger>
              <TabsTrigger value="builder" className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Builder
              </TabsTrigger>
              <TabsTrigger value="system" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                System
              </TabsTrigger>
              <TabsTrigger value="lifecycle" className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Lifecycle
              </TabsTrigger>
            </TabsList>

            {/* ===================== AUTO FLOWS TAB ===================== */}
            <TabsContent value="autoflows">
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Workflow className="w-5 h-5 text-primary" />
                    Auto Flows
                  </CardTitle>
                  <CardDescription>Automated actions that trigger based on client lifecycle events</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  {presetsLoading ? (
                    <div className="flex items-center justify-center h-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : (
                    <div className="space-y-8">
                      {AUTO_FLOW_GROUPS.map((group) => (
                        <div key={group.id}>
                          <div className="mb-3">
                            <h3 className="font-semibold text-foreground">{group.title}</h3>
                            <p className="text-xs text-muted-foreground">{group.description}</p>
                          </div>
                          <div className="space-y-2">
                            {group.presets.map((presetId) => renderPresetRow(presetId))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===================== HISTORY TAB ===================== */}
            <TabsContent value="history">
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        Automation History
                      </CardTitle>
                      <CardDescription>Recent automation activity</CardDescription>
                    </div>
                    <Button onClick={fetchActivityLogs} variant="outline" size="sm" disabled={activityLoading}>
                      {activityLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {activityLoading && activityLogs.length === 0 ? (
                    <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : activityLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="space-y-3 mb-6">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-dashed border-muted-foreground/20">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-muted animate-pulse" />
                              <div className="space-y-1">
                                <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                                <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                              </div>
                            </div>
                            <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                          </div>
                        ))}
                      </div>
                      <p className="text-muted-foreground font-medium">No automation history yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Submit a test lead to see automations in action</p>
                      {forms.length > 0 && (
                        <div className="flex items-center gap-2 mt-4">
                          <Button size="sm" className="rounded-full" onClick={() => { const f = forms[0]; if (f?.slug) window.open(`/form/${f.slug}`, '_blank'); }} data-testid="history-open-form-btn">
                            <Eye className="w-4 h-4 mr-2" />Open your form
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => { const f = forms[0]; if (f?.slug) { navigator.clipboard.writeText(`${window.location.origin}/form/${f.slug}`); toast.success("Link copied!"); } }} data-testid="history-copy-link-btn">
                            <Link2 className="w-4 h-4 mr-2" />Copy form link
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {activityLogs.map((log, idx) => (
                        <div key={log.id || idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getAutomationTypeColor(log.automation_type)}`}>
                              {log.automation_type === "escalation" ? <AlertCircle className="w-4 h-4" /> :
                               log.automation_type === "ai_followup" ? <Sparkles className="w-4 h-4" /> :
                               log.automation_type?.includes("reminder") ? <Clock className="w-4 h-4" /> :
                               <Mail className="w-4 h-4" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{log.client_name}</span>
                                <Badge variant="outline" className="text-xs">{getAutomationTypeLabel(log.automation_type)}</Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>Status: {log.status}</span>
                                {log.template_id && <span>• Template: {log.template_id}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {log.success ? (
                              <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>
                            )}
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeAgo(log.timestamp)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===================== BUILDER TAB ===================== */}
            <TabsContent value="builder">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Automation Builder</h2>
                    <p className="text-sm text-muted-foreground">Create custom automations</p>
                  </div>
                  <Button onClick={handleCreate} className="rounded-xl" data-testid="create-automation-btn">
                    <Plus className="w-4 h-4 mr-2" />New Automation
                  </Button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
                ) : customAutomations.length === 0 ? (
                  <Card className="rounded-2xl border-dashed">
                    <CardContent className="p-12 text-center">
                      <Plus className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No custom automations yet. Auto Flows handle most use cases.</p>
                      <Button onClick={handleCreate} variant="outline" className="mt-4">Create Your First</Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {customAutomations.map((automation) => {
                      const triggerType = automation.trigger_type === "submission_received" ? "form_submitted" : automation.trigger_type;
                      const triggerConfig = TRIGGER_TYPES[triggerType] || TRIGGER_TYPES.form_submitted;
                      const TriggerIcon = triggerConfig.icon || Zap;
                      return (
                        <Card key={automation.id} className={`rounded-2xl shadow-sm border-none ${!automation.is_active ? 'opacity-60' : ''}`}>
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl ${triggerConfig.color} flex items-center justify-center`}>
                                  <TriggerIcon className="w-6 h-6" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-lg">{automation.name}</h3>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-xs">{TRIGGER_TYPES[triggerType]?.label || triggerType}</Badge>
                                    <span className="text-xs text-muted-foreground">{getTriggerSummary(automation)}</span>
                                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">{automation.actions?.length || 0} action(s)</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">{automation.is_active ? "Active" : "Paused"}</span>
                                  <Switch checked={automation.is_active} onCheckedChange={() => handleToggleActive(automation)} />
                                </div>
                                <Button variant="outline" size="sm" onClick={() => handleEdit(automation)}><Edit className="w-4 h-4" /></Button>
                                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => { setAutomationToDelete(automation); setDeleteDialogOpen(true); }}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ===================== SYSTEM TAB ===================== */}
            <TabsContent value="system">
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    System Automations
                  </CardTitle>
                  <CardDescription>Background automations that run automatically</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {systemAutomations.map((auto, idx) => (
                      <div key={idx} className="p-4 bg-muted/30 rounded-xl">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            auto.type === "reminder" ? "bg-yellow-100 text-yellow-700" :
                            auto.type === "escalation" ? "bg-red-100 text-red-700" :
                            auto.type === "ai_followup" ? "bg-purple-100 text-purple-700" :
                            auto.type === "suppression" ? "bg-blue-100 text-blue-700" :
                            "bg-green-100 text-green-700"
                          }`}>
                            {auto.type === "reminder" ? <Clock className="w-5 h-5" /> :
                             auto.type === "escalation" ? <Bell className="w-5 h-5" /> :
                             auto.type === "ai_followup" ? <Sparkles className="w-5 h-5" /> :
                             auto.type === "suppression" ? <Shield className="w-5 h-5" /> :
                             <Zap className="w-5 h-5" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{auto.name}</h4>
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Auto-enabled</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{auto.description}</p>
                            {auto.statuses && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {auto.statuses.map((status, sidx) => (
                                  <Badge key={sidx} variant="secondary" className="text-xs">{status.replace(/_/g, " ")}</Badge>
                                ))}
                              </div>
                            )}
                            {auto.thresholds && (
                              <div className="mt-2 text-xs text-muted-foreground">
                                Thresholds: {Object.entries(auto.thresholds).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}h`).join(" • ")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===================== LIFECYCLE TAB ===================== */}
            <TabsContent value="lifecycle">
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" />
                    Lifecycle Mapping
                  </CardTitle>
                  <CardDescription>How statuses map to automated actions</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid gap-3">
                    {statusMapping.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={`${
                            item.phase === "lead" ? "bg-blue-100 text-blue-700" :
                            item.phase === "intake" ? "bg-purple-100 text-purple-700" :
                            "bg-green-100 text-green-700"
                          }`}>
                            {item.label}
                          </Badge>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{item.template_label}</span>
                          {item.configurable && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">Default</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.has_reminders && (
                            <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-1" />+ Reminders</Badge>
                          )}
                          {item.has_ai_followup && (
                            <Badge variant="outline" className="text-xs"><Sparkles className="w-3 h-3 mr-1" />+ AI Follow-up</Badge>
                          )}
                          {item.is_terminal && (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-200">Terminal</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">How it works</p>
                        <p className="mt-1">When a submission's status changes, the corresponding email template is sent. Edit templates in Settings.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Automation" : "Create Automation"}</DialogTitle>
            <DialogDescription>Set up trigger and actions</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Automation Name *</Label>
              <Input placeholder="e.g., Send welcome email" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            
            <div className="space-y-3">
              <Label>When this happens...</Label>
              <Select value={formData.trigger_type} onValueChange={(value) => setFormData({ ...formData, trigger_type: value, trigger_config: {} })}>
                <SelectTrigger><SelectValue placeholder="Select trigger" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRIGGER_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2"><config.icon className="w-4 h-4" />{config.label}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {formData.trigger_type === "form_submitted" && (
                <div className="pl-4 border-l-2 border-primary/30">
                  <Label className="text-sm">Select Form *</Label>
                  <Select value={formData.trigger_config.form_id || ""} onValueChange={(value) => setFormData({ ...formData, trigger_config: { form_id: value } })}>
                    <SelectTrigger><SelectValue placeholder="Choose a form" /></SelectTrigger>
                    <SelectContent>
                      {forms.length === 0 ? <SelectItem value="no-forms" disabled>No forms</SelectItem> : forms.map(form => <SelectItem key={form.id} value={form.id}>{form.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {formData.trigger_type === "status_changed" && (
                <div className="pl-4 border-l-2 border-primary/30">
                  <Label className="text-sm">When status changes to *</Label>
                  <Select value={formData.trigger_config.to_status || ""} onValueChange={(value) => setFormData({ ...formData, trigger_config: { to_status: value } })}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 text-xs font-semibold text-blue-600">Lead Phase</div>
                      {LEAD_STATUS_OPTIONS.map(status => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}
                      <div className="px-2 py-1.5 text-xs font-semibold text-purple-600 mt-1 border-t">Intake Phase</div>
                      {INTAKE_STATUS_OPTIONS.map(status => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <Label>Then do this...</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(ACTION_TYPES).map(([key, config]) => (
                  <Button key={key} variant="outline" size="sm" onClick={() => addAction(key)} className="text-xs">
                    <config.icon className="w-3 h-3 mr-1" />{config.label}
                  </Button>
                ))}
              </div>
              
              {formData.actions.length === 0 ? (
                <div className="text-center py-8 bg-muted/30 rounded-lg"><p className="text-sm text-muted-foreground">Add an action above</p></div>
              ) : (
                <div className="space-y-3">
                  {formData.actions.map((action, index) => {
                    const actionConfig = ACTION_TYPES[action.type] || {};
                    const ActionIcon = actionConfig.icon || Zap;
                    return (
                      <div key={index} className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">{index + 1}.</span>
                            <Badge className={actionConfig.color}><ActionIcon className="w-3 h-3 mr-1" />{actionConfig.label}</Badge>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeAction(index)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        
                        {action.type === "send_email" && (
                          <Select value={action.config?.template_id || ""} onValueChange={(value) => updateAction(index, { template_id: value })}>
                            <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
                            <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        {action.type === "send_form" && (
                          <Select value={action.config?.form_id || ""} onValueChange={(value) => updateAction(index, { form_id: value })}>
                            <SelectTrigger><SelectValue placeholder="Select form" /></SelectTrigger>
                            <SelectContent>{forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        {action.type === "update_status" && (
                          <Select value={action.config?.status || "new"} onValueChange={(value) => updateAction(index, { status: value })}>
                            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1.5 text-xs font-semibold text-blue-600">Lead</div>
                              {LEAD_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                              <div className="px-2 py-1.5 text-xs font-semibold text-purple-600 mt-1 border-t">Intake</div>
                              {INTAKE_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {action.type === "send_business_notification" && (
                          <Select value={action.config?.template_id || "lead_form_submitted_notification"} onValueChange={(value) => updateAction(index, { template_id: value })}>
                            <SelectTrigger><SelectValue placeholder="Select notification template" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lead_form_submitted_notification">Lead Form Submitted (includes AI score)</SelectItem>
                              {templates.filter(t => t.mode === "internal" || t.name?.includes("Notification")).map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {action.type === "update_contact_info" && (
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={action.config?.field || "source"} onValueChange={(value) => updateAction(index, { field: value })}>
                              <SelectTrigger><SelectValue placeholder="Field" /></SelectTrigger>
                              <SelectContent>{CONTACT_INFO_FIELDS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input placeholder="Value" value={action.config?.value || ""} onChange={(e) => updateAction(index, { value: e.target.value })} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label>Status</Label>
                <p className="text-sm text-muted-foreground">{formData.is_active ? "Active" : "Paused"}</p>
              </div>
              <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editMode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Automation?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete "{automationToDelete?.name}".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preset Detail Dialog */}
      <Dialog open={presetDetailOpen} onOpenChange={setPresetDetailOpen}>
        <DialogContent className="sm:max-w-md">
          {viewingPreset ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const iconConfig = PRESET_ICONS[viewingPreset.presetId] || { icon: Mail, color: "bg-gray-100 text-gray-700" };
                    const Icon = iconConfig.icon;
                    return <div className={`w-8 h-8 rounded-lg ${iconConfig.color} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>;
                  })()}
                  {viewingPreset.name}
                </DialogTitle>
                <DialogDescription>{viewingPreset.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium">Status</span>
                    <Badge variant={viewingPreset.enabled ? "default" : "outline"} className="text-xs">
                      {viewingPreset.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium">Trigger</span>
                    <span className="text-sm text-muted-foreground">{viewingPreset.trigger_display}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium">Email Template</span>
                    <Badge variant="outline" className="text-xs">{viewingPreset.template_label}</Badge>
                  </div>
                  {(() => {
                    const stats = presetStats[viewingPreset.presetId] || {};
                    return stats.last_triggered ? (
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm font-medium">Activity</span>
                        <span className="text-xs text-muted-foreground">Last: {formatTimeAgo(stats.last_triggered)} · {stats.times_triggered} times</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => {
                  const templateId = viewingPreset?.actions?.[0]?.config?.template_id;
                  setPresetDetailOpen(false);
                  navigate(templateId ? `/email-templates?edit=${templateId}` : '/email-templates');
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Email Template
                </Button>
                <Button variant="outline" onClick={() => setPresetDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Couldn't load automation</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
